import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Grid,
  Snackbar,
} from '@mui/material';
import { Visibility as ViewIcon, Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../config/api';
import {
  StyledTableContainer,
  TableSkeleton,
  FilterBar,
  FilterSelect,
  SearchInput,
  StyledPagination,
  EmptyState,
  StatusChip,
} from '../components/TableShared';

const ff = "'Poppins', sans-serif";

const STATUS_COLORS = {
  PENDING: 'warning',
  CONFIRMED: 'info',
  ONGOING: 'primary',
  COMPLETED: 'success',
  CANCELLED: 'error',
  DECLINED: 'error',
  EXPIRED: 'default',
};

const STATUS_CHIP_MAP = {
  PENDING: { color: '#D69E2E', bgColor: '#FFFFF0' },
  CONFIRMED: { color: '#3182CE', bgColor: '#EBF8FF' },
  ONGOING: { color: '#805AD5', bgColor: '#FAF5FF' },
  COMPLETED: { color: '#38A169', bgColor: '#F0FFF4' },
  CANCELLED: { color: '#E53E3E', bgColor: '#FFF5F5' },
  DECLINED: { color: '#C05050', bgColor: '#FFF5F5' },
  EXPIRED: { color: '#8A8A8A', bgColor: '#F5F5F5' },
};

const ALL_STATUSES = ['PENDING', 'CONFIRMED', 'ONGOING', 'COMPLETED', 'CANCELLED', 'DECLINED', 'EXPIRED'];

function formatDateNice(dateString) {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy hh:mm a');
  } catch {
    return 'N/A';
  }
}

// ----- BookingDetailDialog -----
function BookingDetailDialog({ open, booking, onClose, onStatusUpdated }) {
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (booking) {
      setNewStatus(booking.status || '');
    }
  }, [booking]);

  if (!booking) return null;

  const handleUpdateStatus = async () => {
    if (!newStatus || newStatus === booking.status) return;
    try {
      setUpdating(true);
      await api.put(`/admin/bookings/${booking.id}/status`, { status: newStatus });
      setSnackbar({ open: true, message: 'Booking status updated successfully.', severity: 'success' });
      onStatusUpdated();
    } catch (err) {
      console.error('Error updating booking status:', err);
      const msg = err.response?.data?.message || 'Failed to update booking status.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const InfoRow = ({ label, value }) => (
    <>
      <Grid item xs={4}>
        <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontFamily: ff }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2" sx={{ fontFamily: ff }}>{value || 'N/A'}</Typography>
      </Grid>
    </>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: ff }}>
          Booking Details
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Booking Info */}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontFamily: ff }}>
            Booking Info
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <InfoRow label="Booking ID" value={booking.id ? `#${String(booking.id).slice(-6)}` : undefined} />
            <InfoRow label="Service Type" value={booking.serviceType} />
            <InfoRow label="Start Date" value={formatDateNice(booking.startDate || booking.date)} />
            <InfoRow label="End Date" value={formatDateNice(booking.endDate)} />
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontFamily: ff }}>
                Status
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Chip
                label={booking.status || 'N/A'}
                color={STATUS_COLORS[booking.status] || 'default'}
                size="small"
              />
            </Grid>
            <InfoRow
              label="Total Amount"
              value={
                booking.totalAmount != null
                  ? `$${Number(booking.totalAmount).toFixed(2)}`
                  : undefined
              }
            />
            <InfoRow label="Notes" value={booking.notes} />
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* User Info */}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontFamily: ff }}>
            User Info
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <InfoRow
              label="Name"
              value={booking.user?.fullName || booking.user?.name}
            />
            <InfoRow label="Email" value={booking.user?.email} />
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Pet Info */}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontFamily: ff }}>
            Pet Info
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <InfoRow
              label="Name"
              value={booking.pet?.name || booking.pet?.petName}
            />
            <InfoRow
              label="Type"
              value={booking.pet?.type || booking.pet?.petType}
            />
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Sitter Info */}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontFamily: ff }}>
            Sitter Info
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <InfoRow
              label="Name"
              value={booking.sitter?.fullName || booking.sitter?.name}
            />
            <InfoRow label="Email" value={booking.sitter?.email} />
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Status Update */}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontFamily: ff }}>
            Update Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontFamily: ff }}>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => setNewStatus(e.target.value)}
                sx={{ fontFamily: ff }}
              >
                {ALL_STATUSES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontFamily: ff }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleUpdateStatus}
              disabled={updating || !newStatus || newStatus === booking.status}
              sx={{ fontFamily: ff }}
            >
              {updating ? <CircularProgress size={20} /> : 'Update Status'}
            </Button>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} sx={{ fontFamily: ff }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// ----- Bookings Page -----
export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalBookings, setTotalBookings] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Detail dialog state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }
      if (search) {
        params.search = search;
      }
      const response = await api.get('/admin/bookings', { params });
      const data = response.data.data || response.data;
      setBookings(data.bookings || []);
      setTotalBookings(data.total || data.bookings?.length || 0);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Failed to load bookings. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, search]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleDeleteBooking = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/bookings/${deleteTarget.id}`);
      setSnackbar({ open: true, message: 'Booking deleted', severity: 'success' });
      setDeleteTarget(null);
      fetchBookings();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete booking', severity: 'error' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedBooking(null);
  };

  const handleStatusUpdated = () => {
    handleCloseDetail();
    fetchBookings();
  };

  return (
    <Box>
      {/* Filter Bar */}
      <FilterBar>
        <SearchInput
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search by user, sitter, service..."
        />
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          options={[
            { value: 'ALL', label: 'All' },
            { value: 'PENDING', label: 'Pending' },
            { value: 'CONFIRMED', label: 'Confirmed' },
            { value: 'ONGOING', label: 'Ongoing' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'CANCELLED', label: 'Cancelled' },
            { value: 'DECLINED', label: 'Declined' },
            { value: 'EXPIRED', label: 'Expired' },
          ]}
        />
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton columns={7} rows={6} />
      ) : (
        <>
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Sitter</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>
                      {booking.id ? `#${String(booking.id).slice(-6)}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {booking.user
                        ? booking.user.fullName || booking.user.email
                        : booking.userId || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {booking.sitter
                        ? booking.sitter.fullName || booking.sitter.email
                        : booking.sitterId || 'N/A'}
                    </TableCell>
                    <TableCell>{booking.serviceType || 'N/A'}</TableCell>
                    <TableCell>{formatDate(booking.date || booking.startDate)}</TableCell>
                    <TableCell>
                      <StatusChip
                        label={booking.status || 'N/A'}
                        color={STATUS_CHIP_MAP[booking.status]?.color || '#718096'}
                        bgColor={STATUS_CHIP_MAP[booking.status]?.bgColor || '#F7FAFC'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewBooking(booking)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ color: '#E53E3E' }}
                        onClick={() => setDeleteTarget(booking)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {bookings.length === 0 && (
                  <EmptyState colSpan={7} message="No bookings found." />
                )}
              </TableBody>
            </Table>
            <StyledPagination
              count={totalBookings}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </StyledTableContainer>
        </>
      )}

      {/* Booking Detail Dialog */}
      <BookingDetailDialog
        open={detailOpen}
        booking={selectedBooking}
        onClose={handleCloseDetail}
        onStatusUpdated={handleStatusUpdated}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle sx={{ fontFamily: ff, fontWeight: 600 }}>Delete Booking?</DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: ff, fontSize: 13, color: '#718096' }}>
            This will permanently remove booking #{deleteTarget?.id?.slice(-6)} ({deleteTarget?.serviceType}).
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ fontFamily: ff, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDeleteBooking} variant="contained" color="error" sx={{ fontFamily: ff, textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
